import React, { useMemo, useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import { Box } from '@mui/material'
import PageWrapper from 'Pages/@PageWrapper'
import { AppFilterPanel, AppPageTabs, AppInfoButton, AppNote } from '@components'
import { Surface } from 'Pages/DsRepricing/Setup/@components/Surface'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import { dsColorNew } from '@palette'

import CalendarTab from './Calendar'
import RulesTab    from './Rules'
import { LegacyReadOnlyAlert, useMigrationPending } from 'Pages/DsRepricing/@components/MigrationBanner'

/**
 * DS Repricing — Schedule. LEGACY since the 28 re-cut (CU-869fhuhy6, UX
 * D10/D11): `/schedule` bounces to Executions; this window is reached only
 * from the migration banner, read-only, until the migration is validated.
 *
 * Two tabs:
 *   Calendar — month-view of what the engine will do each day: the
 *              schedule rule that governs the frequency + the
 *              strategies matched (and the ones that did NOT match,
 *              with the reason). Filterable by Office / ACRISS.
 *   Rules    — CRUD of the schedule rules themselves. Rules cannot
 *              overlap on the same (office, acriss) scope; different
 *              scopes are allowed to coexist over the same DBA range.
 *
 * Phase-1 UI: front-only, backed by localStorage mocks. Backend engine
 * lands once the shape is validated with the operator.
 */
const TABS = [
	{ key: 'calendar', label: 'Calendar', Component: CalendarTab },
	{ key: 'rules',    label: 'Schedule rules', Component: RulesTab },
	// Executions is its own entry now (`Pages/DsRepricing/Executions`).
]

export default function DsRepricingSchedulePage() {
	const [searchParams, setSearchParams] = useSearchParams()

	const initialTab = useMemo(() => {
		const t = searchParams.get('tab')
		return TABS.some(x => x.key === t) ? t : TABS[0].key
	}, [])
	const [activeKey, setActiveKey] = useState(initialTab)

	const setTab = (key) => {
		setActiveKey(key)
		const next = new URLSearchParams(searchParams)
		next.set('tab', key)
		setSearchParams(next, { replace: true })
	}

	const active = TABS.find(t => t.key === activeKey) || TABS[0]
	const Body = active.Component
	const pending = useMigrationPending()

	// `null` while the server has not answered: nothing retired on a guess.
	if (pending === false) {
		return (
			<PageWrapper title={DS_REPRICING_ROUTES.SCHEDULE_LEGACY_TITLE} subtitle={DS_REPRICING_ROUTES.SCHEDULE_SUBTITLE}>
				<AppNote>
					Retired — the migration into Strategies was validated. What ran is in{' '}
					<Box component={RouterLink} to={DS_REPRICING_ROUTES.EXECUTIONS_PATH}
					     sx={{ color: dsColorNew.appBlue, fontWeight: 600 }}>Executions</Box>; when a strategy runs is on the strategy itself.
				</AppNote>
			</PageWrapper>
		)
	}

	return (
		<PageWrapper
			title={DS_REPRICING_ROUTES.SCHEDULE_LEGACY_TITLE}
			// What the page was for, behind an ⓘ beside the subtitle (it opened the page as a paragraph)
			subtitle={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
				{DS_REPRICING_ROUTES.SCHEDULE_SUBTITLE}
				<AppInfoButton title={DS_REPRICING_ROUTES.SCHEDULE_LEGACY_TITLE} size={18}>
					<span>
						Program the repricing engine. <strong>Schedule rules</strong>{' '}
						decide how often the engine runs for each pickup window
						(scoped by office / acriss when needed). The{' '}
						<strong>Calendar</strong> tab overlays those rules with the
						strategies that will match each day — including the ones
						that will NOT match, so you can audit why.
					</span>
				</AppInfoButton>
			</Box>}
		>
			<LegacyReadOnlyAlert/>
			{/* The page's white band, as Setup: its tabs */}
			<AppFilterPanel>
				<AppPageTabs value={activeKey} onChange={(_, k) => setTab(k)}
				             stackProps={{ sx: { mb: 0 } }}
				             sx={{ '& .MuiTabs-flexContainer > .MuiTab-root': { mr: 0 } }}
				             tabs={TABS.map(t => ({ key: t.key, value: t.key, label: t.label }))}/>
			</AppFilterPanel>

			{/* Read-only while it is kept (UX D11): the controls are there to read, not to use */}
			<Surface sx={{ p: 2.5, overflow: 'visible', ...setupScopeSx }}>
				<Box inert="" sx={{ pointerEvents: 'none', opacity: 0.7, userSelect: 'none' }}>
					<Body/>
				</Box>
			</Surface>
		</PageWrapper>
	)
}
